// The Session View's launch rules, in one place: they are Live's, not a page's,
// and both surfaces that launch clips — a jam room and a stored seed — must
// mean the same thing by a scene launch.

import { isLooped } from "@web-shared/lib/clip-loop";
import { beatsPerBar, FOUR_FOUR, type Meter } from "@web-shared/lib/meter";
import { ASSERT_EPS } from "@shared/sync/canonical.js";
import type { Lane, LaneClip } from "@web-shared/lib/arrangement";
import { oneOf } from "@web-shared/hooks/usePref";

/** Launch timing, as Live's chooser offers it. */
export type LaunchQuantize = "bar" | "beat" | "none";
export const isLaunchQuantize = oneOf(["bar", "beat", "none"] as const);

/**
 * Whether a slot clip has anything to sound.
 *
 * A MIDI clip is synthesised, so it always does. An audio clip plays its own
 * object and nothing else — a lane's stem is a render of the TIMELINE and
 * never covers a slot — so one without a key is not launchable.
 */
export function launchableSlotClip(clip: LaneClip): boolean {
  return clip.kind === "midi" || Boolean(clip.audioKey);
}

/** The shortest piece of material that can stand as a loop: one bar. */
export const LOOP_MIN_BARS = 1;

/**
 * Whether a clip's length can carry a loop: at least a bar, and a whole
 * number of them. Counted in the meter it is given, because a bar is three
 * beats at 3/4 and the tell is bars, not beats.
 *
 * This used to decide which timeline clips became pads at all
 * (lib/ghost-clips.ts). It decides something narrower now — every distinct
 * piece of material is offered as a pad, and this says whether hitting it
 * loops or plays it once.
 */
export function looksLikeLoop(clip: LaneClip, meter: Meter = FOUR_FOUR): boolean {
  const bar = beatsPerBar(meter);
  const min = bar * LOOP_MIN_BARS;
  if (!(bar > 0) || !(clip.duration >= min - ASSERT_EPS)) return false;
  const bars = clip.duration / bar;
  return Math.abs(bars - Math.round(bars)) * bar <= ASSERT_EPS;
}

/**
 * Whether launching a clip loops it, or plays it once.
 *
 * Live loops a slot clip it has braced, and a loop is what a pad is FOR — but
 * a pad is now offered for every distinct piece of material on a track, and
 * some of that material is a 3.4-bar slice of an arrangement. Looping one of
 * those does not give you a loop, it gives you a stumble every 3.4 bars. So:
 * the producer's own brace wins, a length that sits on the bar grid loops
 * because it can, and anything else is a one-shot.
 *
 * MIDI has no geometry on the wire (its loop is flattened into the notes):
 * the Set's own Loop switch decides when the room knows it, and a clip from
 * before it was synced loops, as Live's MIDI slot clips do by default.
 */
export function loopsOnLaunch(clip: LaneClip, meter: Meter = FOUR_FOUR): boolean {
  if (clip.kind === "midi") return clip.slotLooping ?? true;
  if (!clip.geometry) return true;
  return isLooped(clip.geometry) || looksLikeLoop(clip, meter);
}

export interface SceneLaunchPlan {
  /** The clips the row starts, in lane order. */
  launch: Array<{ lane: Lane; clip: LaneClip }>;
  /** Lanes the row silences — Live's stop button in an empty slot. */
  stop: string[];
}

/**
 * What launching scene `row` does: every clip in the row starts, and a lane
 * whose slot there is empty (or holds a clip with nothing to play) stops. A
 * scene is a whole state of the Set, not just the clips it names.
 */
export function sceneLaunchPlan(lanes: readonly Lane[], row: number): SceneLaunchPlan {
  const plan: SceneLaunchPlan = { launch: [], stop: [] };
  for (const lane of lanes) {
    const clip = lane.sessionClips.find((c) => c.slot === row);
    if (clip && launchableSlotClip(clip)) plan.launch.push({ lane, clip });
    else plan.stop.push(lane.id);
  }
  return plan;
}
